import { useEffect, useRef } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// Edge-swipe navigation for the mobile sidebar (≤900px band), added 2026-10-05.
// v2 (same day, after Amine's on-device test): zone raised 24→32px + the
// OPEN gesture now COMPETES WITH Chrome-for-Android's left-edge browser
// BACK-SWIPE (v1 was fully passive, so Chrome won the drag and the sidebar
// never opened on his phone).
//
// Gesture model (v2):
//   OPEN — the touch must START within EDGE_ZONE_PX (32px, 0-inclusive) of the
//     left screen edge. At touchstart we bail if a visible MUI modal is open
//     (dialogs, mobile pickers, menus, popovers) or if the touch sits inside a
//     container that is already horizontally scrolled (scrollLeft > 0) so
//     mobile tables keep their rightward pan. The first movement locks the
//     axis: a vertical-dominant start (>10px, |dy| > |dx|) is abandoned to
//     the native scroll; a rightward-dominant start (≥6px forward and
//     forward > 1.2×|dy|) locks horizontal — and from that exact event on,
//     every touchmove of this finger is preventDefault()ed (cancelable
//     guard). Consuming the touch stream is what makes Chrome-for-Android
//     abandon its browser-level back-swipe: a preview that already started
//     snaps back and the navigation never commits. At 64px of rightward
//     travel the sidebar opens immediately (single-shot); the standard MUI
//     slide-in plays — identical to the Navbar hamburger path.
//   CLOSE — when the sidebar is open, a left-dominant drag past
//     CLOSE_THRESHOLD_PX (64px) closes it; the drag may start anywhere.
//     Close stays fully PASSIVE: a left-drag is not a browser edge gesture
//     on Android Chrome and the drawer's own list keeps scrolling natively.
//
// preventDefault policy (strict):
//   - never on touchstart (a canceled touchstart also cancels the click);
//   - never before the axis decision;
//   - never for taps (<6px of travel), vertical scrolls (they abort lock-
//     free), or any touch that did NOT start inside the edge zone;
//   - ONLY for open-mode gestures that locked horizontal — on the lock event
//     itself and on every later move of that finger until touchend.
//   Taps and clicks therefore pass through byte-identically to v1, including
//   taps on the navbar hamburger/back button that sit inside the zone.
//
// Listener passivity: touchstart/touchend/touchcancel stay PASSIVE;
// touchmove is NON-PASSIVE so the lock-time preventDefault is honored.
// Trade-off accepted: a window-level non-passive touchmove asks the browser
// to wait for JS per scroll frame in the ≤900px band. There is no element at
// the screen edge to bind to, and attaching mid-gesture is unreliable
// (Chrome fixes gesture ownership at touchstart). The handler is a few
// integer comparisons; if mobile scrolling feels heavier, the fallback is
// on-demand attachment (weaker vs Chrome) — flagged for review, not done
// preemptively.
//
// Platform honesty: Android OS gesture-navigation (system-level) reserves the
// extreme edge on some devices — those touches never reach the page and no
// page code can suppress them. iOS Safari's left-edge back-swipe is likewise
// not preventable from the page today. Chrome's BROWSER-level back-swipe IS
// expected to yield to the preventDefault stream above.
//
// Why not MUI SwipeableDrawer: (unchanged) the drawer unmounts while closed
// (`{activeMenu && <Drawer/>}` in Sidebar.jsx) and is `permanent` in the
// 600–899px band, so pull-to-open would require restructuring the drawer's
// mount/variant model — risking visual and behavioral parity. This hook
// instead drives the exact same context state the hamburger uses.
// ─────────────────────────────────────────────────────────────────────────────

export const EDGE_ZONE_PX = 32;
export const OPEN_THRESHOLD_PX = 64;
export const CLOSE_THRESHOLD_PX = 64;

// Internal tuning
const AXIS_LOCK_PX = 6; // rightward travel that locks the horizontal axis —
// deliberately lower than the vertical-abort threshold so the stream-owning
// preventDefault lands as early as possible in an edge drag (beats Chrome's
// back-swipe). Taps (<6px) can never lock, so they always pass through.
const DIRECTION_LOCK_PX = 10; // vertical-abort threshold (|dy| > |dx| and >10)
const HORIZONTAL_DOMINANCE = 1.2; // forward px must beat |dy| by this factor
const MAX_SCROLLER_WALK = 12; // how far up the DOM to look for h-scrollers

const findTouch = (touchList, identifier) => {
  for (let i = 0; i < touchList.length; i += 1) {
    if (touchList[i].identifier === identifier) return touchList[i];
  }
  return null;
};

// A visible MUI overlay (dialog / mobile picker / menu / popover) owns the
// screen — the sidebar gesture must not fire underneath it. Modals that stay
// mounted but hidden (inline visibility:hidden when closed) are ignored.
const hasVisibleModal = () => {
  const modals = document.querySelectorAll('[class*="MuiModal-root"]');
  for (let i = 0; i < modals.length; i += 1) {
    const styles = window.getComputedStyle(modals[i]);
    if (styles.visibility !== "hidden" && styles.display !== "none") {
      return true;
    }
  }
  return false;
};

// If the touch starts inside a container that is already scrolled right
// (scrollLeft > 0), a rightward drag is a pan-back for that container — leave
// the gesture to it (e.g. the Timesheets horizontal table). At scrollLeft 0 a
// rightward pan is a no-op on the container, so the drawer gesture may proceed.
const isInsideScrolledHorizontalArea = (target) => {
  if (!(target instanceof Element)) return false;
  let el = target;
  let depth = 0;
  while (el && el !== document.body && depth < MAX_SCROLLER_WALK) {
    if (el.scrollWidth - el.clientWidth > 4) {
      const overflowX = window.getComputedStyle(el).overflowX;
      if (
        (overflowX === "auto" ||
          overflowX === "scroll" ||
          overflowX === "overlay") &&
        el.scrollLeft > 4
      ) {
        return true;
      }
    }
    el = el.parentElement;
    depth += 1;
  }
  return false;
};

export const useEdgeSwipeToOpen = ({
  enabled,
  isOpen,
  onOpen,
  onClose,
  edgeZonePx = EDGE_ZONE_PX,
  openThresholdPx = OPEN_THRESHOLD_PX,
  closeThresholdPx = CLOSE_THRESHOLD_PX,
}) => {
  const isOpenRef = useRef(isOpen);
  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);
  const configRef = useRef({ edgeZonePx, openThresholdPx, closeThresholdPx });
  const gestureRef = useRef(null);

  // Latest-value refs: the listeners are attached once per `enabled` change
  // and must not re-attach mid-gesture when state/props change.
  isOpenRef.current = isOpen;
  onOpenRef.current = onOpen;
  onCloseRef.current = onClose;
  configRef.current = { edgeZonePx, openThresholdPx, closeThresholdPx };

  useEffect(() => {
    if (!enabled) {
      gestureRef.current = null;
      return undefined;
    }

    const handleTouchStart = (event) => {
      gestureRef.current = null; // any new touch cancels a pending gesture
      if (event.touches.length !== 1) return; // multi-touch (pinch) — ignore

      const touch = event.touches[0];
      const wasOpen = isOpenRef.current;

      if (!wasOpen) {
        if (touch.clientX < 0 || touch.clientX > configRef.current.edgeZonePx) {
          return; // must start inside the left edge zone
        }
        if (hasVisibleModal()) return;
        if (isInsideScrolledHorizontalArea(event.target)) return;
      }

      gestureRef.current = {
        identifier: touch.identifier,
        startX: touch.clientX,
        startY: touch.clientY,
        mode: wasOpen ? "close" : "open",
        axis: null,
        fired: false,
      };
    };

    const handleTouchMove = (event) => {
      const gesture = gestureRef.current;
      if (!gesture) return;

      if (event.touches.length > 1) {
        gestureRef.current = null; // a second finger landed — not our gesture
        return;
      }

      const touch = findTouch(event.changedTouches, gesture.identifier);
      if (!touch) return;

      // An open-mode gesture that already locked horizontal owns this touch
      // stream until the finger lifts: keep consuming every move so Chrome
      // for Android cannot (re)engage its left-edge back-swipe. The
      // `cancelable` guard skips moves the browser already routed to a
      // native scroll (preventDefault would be a no-op + console noise).
      const consume = () => {
        if (event.cancelable) event.preventDefault();
      };
      if (gesture.axis === "horizontal" && gesture.mode === "open") {
        consume();
      }
      if (gesture.fired) return; // single-shot — no re-fires, no jitter

      const dx = touch.clientX - gesture.startX;
      const dy = touch.clientY - gesture.startY;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      // Fingers move right to open, left to close — reduce both to "forward".
      const forward = gesture.mode === "open" ? dx : -dx;

      if (gesture.axis === null) {
        // Vertical intent wins — let the page scroll natively. No
        // preventDefault was ever called for this gesture, so the scroll is
        // fully native (identical to v1).
        if (absDy > DIRECTION_LOCK_PX && absDy > absDx) {
          gestureRef.current = null;
          return;
        }
        const horizontalIntent =
          forward >= AXIS_LOCK_PX &&
          forward > absDy * HORIZONTAL_DOMINANCE;
        if (!horizontalIntent) return; // keep waiting for an axis decision
        gesture.axis = "horizontal";
        if (gesture.mode === "open") consume(); // page owns the stream NOW
      }

      const threshold =
        gesture.mode === "open"
          ? configRef.current.openThresholdPx
          : configRef.current.closeThresholdPx;

      if (forward >= threshold) {
        // Single-shot: no re-fires, no jitter. In OPEN mode the gesture is
        // kept in the ref until touchend so the moves that follow the open
        // stay preventDefault()ed (Chrome must not re-engage mid-drag).
        gesture.fired = true;
        if (gesture.mode === "open") {
          onOpenRef.current?.();
        } else {
          onCloseRef.current?.();
        }
      }
    };

    const handleTouchEnd = (event) => {
      const gesture = gestureRef.current;
      if (!gesture) return;
      if (!findTouch(event.touches, gesture.identifier)) {
        gestureRef.current = null;
      }
    };

    // touchstart/touchend/touchcancel stay passive (we never preventDefault
    // there — a canceled touchstart would also cancel the click). touchmove
    // is non-passive because the open gesture must be able to preventDefault
    // once the axis lock decides (see header for the exact policy).
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });
    window.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
      gestureRef.current = null;
    };
  }, [enabled]);
};

export default useEdgeSwipeToOpen;
